import { useCallback, useEffect, useRef, useState } from "react";

import {
  useSocketEvent,
  useSocketResync,
  useSocketRoom,
} from "./useSocketEvent";
import type {
  ContainerMessage,
  ContainerMessageEvent,
  ContainerMessageReadEvent,
  ContainerMessageSide,
  ContainerMessageTemplateKey,
  ContainerMessagePage,
  ContainerMessaging,
} from "../types/containerMessages";

/**
 * One container's conversation — fetch it, watch it, add to it.
 *
 * Shared by the cab and the office because the conversation is the same object
 * seen from two ends: only the four calls differ (a driver's are scoped to
 * their truck, a dispatcher's to their company), and they are passed in. The
 * behaviour that must not diverge — when a room is joined, when a thread is
 * marked read, how an optimistic message is reconciled — lives here once.
 *
 * ── What this deliberately does not do ──────────────────────────────────────
 *
 * It does not fetch anything until the thread is actually opened. A container
 * screen shows a badge, and a badge comes off the counters the container itself
 * already carries — so the words are fetched when somebody looks at them and
 * not before. On a driver's list of twelve containers that is the difference
 * between one request and twelve.
 *
 * It does not poll. The room is joined for exactly as long as the thread is on
 * screen, so a message arrives because the server pushed it into a room with
 * one or two people in it — see backend/socket/emitters.js for why that
 * matters at a thousand containers.
 */

export interface ContainerMessageTransport {
  list: (params?: {
    before?: string;
    limit?: number;
  }) => Promise<{ data: { data: ContainerMessagePage } }>;
  send: (
    data:
      | { body: string; templateKey?: never }
      | { templateKey: ContainerMessageTemplateKey; body?: never },
  ) => Promise<{
    data: { data: { message: ContainerMessage; messaging: ContainerMessaging } };
  }>;
  markRead: () => Promise<{ data: { data: { messaging: ContainerMessaging } } }>;
}

interface Options {
  /** The container whose thread this is. Falsy while nothing is open. */
  lineId: string | null | undefined;
  /** Which end of the channel this app is. */
  side: ContainerMessageSide;
  /**
   * Whether the thread is on screen right now.
   *
   * Drives everything: the first fetch, the room membership and the read
   * receipt. A tab that is mounted but hidden must not clear the other side's
   * badge — nobody has read anything.
   */
  open: boolean;
  transport: ContainerMessageTransport;
  /**
   * Told whenever the counters move, so the page holding the container can keep
   * its own badge in step without re-fetching it.
   */
  onMessaging?: (messaging: ContainerMessaging) => void;
}

const byId = (messages: ContainerMessage[]) => new Set(messages.map((m) => m._id));

export function useContainerMessages({
  lineId,
  side,
  open,
  transport,
  onMessaging,
}: Options) {
  const [messages, setMessages] = useState<ContainerMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  /**
   * Held in a ref as well as in state so the socket handlers can read the
   * current thread without being re-bound on every message — the handler is
   * stable, the data is not.
   */
  const transportRef = useRef(transport);
  transportRef.current = transport;
  const onMessagingRef = useRef(onMessaging);
  onMessagingRef.current = onMessaging;

  const publish = useCallback((messaging: ContainerMessaging) => {
    onMessagingRef.current?.(messaging);
  }, []);

  /**
   * The room is joined while the thread is open and left when it is not.
   *
   * This is the whole of the traffic control. A message is emitted into this
   * room only, so a driver looking at CO001-01 is not sent a word about
   * CO001-02 — they are sent that thread's counters instead, in a room they are
   * already in, and their screen draws a badge. Open CO001-02 and the words
   * arrive here rather than there.
   */
  useSocketRoom("containerLine", open && lineId ? lineId : null);

  const load = useCallback(async () => {
    if (!lineId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await transportRef.current.list();
      const page = res.data.data;
      setMessages(page.messages ?? []);
      setCursor(page.cursor ?? null);
      setHasMore(Boolean(page.hasMore));
      if (page.messaging) publish(page.messaging);
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      setError(message ?? "loadFailed");
    } finally {
      setLoading(false);
    }
  }, [lineId, publish]);

  /** The page above the oldest message held. */
  const loadOlder = useCallback(async () => {
    if (!lineId || !cursor || loading) return;
    setLoading(true);
    try {
      const res = await transportRef.current.list({ before: cursor });
      const page = res.data.data;
      setMessages((current) => {
        const known = byId(current);
        return [...(page.messages ?? []).filter((m) => !known.has(m._id)), ...current];
      });
      setCursor(page.cursor ?? null);
      setHasMore(Boolean(page.hasMore));
    } catch {
      // Silent: failing to reach further back must not disturb a conversation
      // that is otherwise working. The button stays, and stays pressable.
    } finally {
      setLoading(false);
    }
  }, [cursor, lineId, loading]);

  // Opening the thread fetches it; closing it drops what was held, so
  // re-opening a different container can never show the previous one's words
  // for a frame.
  useEffect(() => {
    if (!open || !lineId) {
      setMessages([]);
      setCursor(null);
      setHasMore(false);
      return;
    }
    load();
  }, [open, lineId, load]);

  /**
   * Mark the thread read once it has actually been shown.
   *
   * After the fetch, not with it: fetching is not reading. A background resync
   * or a list preview would otherwise clear the badge protecting a message
   * nobody has looked at.
   *
   * Called unconditionally when the thread opens and after each new message
   * arrives into an open thread — it is idempotent, and the server writes
   * nothing when there is nothing outstanding, so the client is spared having
   * to work out whether it needs to ask.
   */
  const markRead = useCallback(async () => {
    if (!open || !lineId) return;
    try {
      const res = await transportRef.current.markRead();
      const messaging = res.data.data?.messaging;
      if (messaging) publish(messaging);
    } catch {
      // A read receipt that did not land is a badge that clears on the next
      // open. Never worth an error in front of a driver at a gate.
    }
  }, [lineId, open, publish]);

  useEffect(() => {
    if (!open || !lineId || loading) return;
    markRead();
    // Deliberately keyed on the thread and its length: a message arriving into
    // an open conversation has been read the moment it is rendered.
  }, [open, lineId, loading, messages.length, markRead]);

  // ── Realtime ──────────────────────────────────────────────────────────────

  useSocketEvent<ContainerMessageEvent>(
    "containerMessage:new",
    (payload) => {
      // The room already scopes this; the check covers the gap between a fast
      // navigation and the server honouring the leave.
      if (!lineId || payload?.lineId !== lineId) return;
      setMessages((current) =>
        current.some((m) => m._id === payload.message._id)
          ? // Our own message, already placed by the send below. Replacing it
            // rather than skipping keeps the server's copy authoritative — it
            // carries the stamped context and the canonical body.
            current.map((m) =>
              m._id === payload.message._id ? payload.message : m,
            )
          : [...current, payload.message],
      );
    },
    Boolean(open && lineId),
  );

  useSocketEvent<ContainerMessageReadEvent>(
    "containerMessage:read",
    (payload) => {
      if (!lineId || payload?.lineId !== lineId) return;
      // The other side caught up: everything this side sent is now seen. Only
      // the tick changes, so the messages are patched in place rather than
      // refetched.
      if (payload.side === side) return;
      setMessages((current) =>
        current.map((m) =>
          m.from === side && !m.readAt ? { ...m, readAt: payload.readAt } : m,
        ),
      );
    },
    Boolean(open && lineId),
  );

  // Events raised while the socket was down are gone — there is no replay — so
  // an open conversation refetches rather than quietly missing a message.
  useSocketResync(() => {
    if (open && lineId) load();
  }, Boolean(open && lineId));

  // ── Sending ───────────────────────────────────────────────────────────────

  const send = useCallback(
    async (
      data:
        | { body: string; templateKey?: never }
        | { templateKey: ContainerMessageTemplateKey; body?: never },
    ) => {
      if (!lineId || sending) return false;
      setSending(true);
      try {
        const res = await transportRef.current.send(data);
        const { message, messaging } = res.data.data;
        // Appended here as well as by the socket echo: the request may well
        // beat its own broadcast, and a message that appears twice is worse
        // than one that appears once from either source. Deduped by id.
        setMessages((current) =>
          current.some((m) => m._id === message._id)
            ? current
            : [...current, message],
        );
        if (messaging) publish(messaging);
        return true;
      } catch (e: unknown) {
        const message = (e as { response?: { data?: { message?: string } } })
          ?.response?.data?.message;
        setError(message ?? "sendFailed");
        return false;
      } finally {
        setSending(false);
      }
    },
    [lineId, publish, sending],
  );

  return {
    messages,
    loading,
    sending,
    error,
    hasMore,
    loadOlder,
    send,
    reload: load,
    clearError: () => setError(null),
  };
}
